import { z } from 'zod';

// Packaging format for supplier offerings
export const packagingFormatSchema = z.object({
  unitSize: z.number(),
  unitOfMeasure: z.string(),
  unitsPerCase: z.number().optional(),
  casesPerPallet: z.number().optional(),
  description: z.string().optional(),
});

export type PackagingFormat = z.infer<typeof packagingFormatSchema>;

// Supplier offering for a catalog item
export const itemSupplierSchema = z.object({
  supplierId: z.string(),
  supplierName: z.string(),
  // Supplier-specific product codes
  supplierSku: z.string().optional(),
  supplierProductName: z.string().optional(),
  ean: z.string().optional(),
  upc: z.string().optional(),
  dun14: z.string().optional(),
  gtin: z.string().optional(),
  // Pricing
  unitPrice: z.number(),
  casePrice: z.number().optional(),
  currency: z.string(),
  // Packaging
  packaging: packagingFormatSchema.optional(),
  // Logistics
  leadTimeDays: z.number(),
  minOrderQuantity: z.number(),
  orderMultiple: z.number().optional(),
  maxOrderQuantity: z.number().optional(),
  // Status
  isPreferred: z.boolean(),
  isActive: z.boolean().optional(),
  // History
  lastPurchaseDate: z.string().optional(),
  lastPurchasePrice: z.number().optional(),
  lastPurchaseQuantity: z.number().optional(),
  // Notes
  notes: z.string().optional(),
});

export type ItemSupplier = z.infer<typeof itemSupplierSchema>;

// Cost history entry for price trends
export const costHistoryEntrySchema = z.object({
  date: z.string(),
  unitCost: z.number(),
  supplierId: z.string(),
  supplierName: z.string(),
  poNumber: z.string().optional(),
});

export type CostHistoryEntry = z.infer<typeof costHistoryEntrySchema>;

// Attachment/document for a catalog item
export const itemAttachmentSchema = z.object({
  attachmentId: z.string(),
  fileName: z.string(),
  fileType: z.string(),
  fileSize: z.number(),
  url: z.string(),
  uploadedBy: z.string(),
  uploadedAt: z.string(),
  description: z.string().optional(),
});

export type ItemAttachment = z.infer<typeof itemAttachmentSchema>;

// Related item (substitute or similar)
export const relatedItemSchema = z.object({
  itemId: z.string(),
  sku: z.string(),
  name: z.string(),
  relationshipType: z.enum(['substitute', 'similar', 'component', 'parent']),
  conversionFactor: z.number().optional(),
  notes: z.string().optional(),
});

export type RelatedItem = z.infer<typeof relatedItemSchema>;

// Recipe usage reference
export const recipeUsageSchema = z.object({
  recipeId: z.string(),
  recipeName: z.string(),
  recipeType: z.enum(['finished', 'sub-recipe', 'component']),
  quantityPerServing: z.number(),
  unit: z.string(),
});

export type RecipeUsage = z.infer<typeof recipeUsageSchema>;

// Stock level summary
export const stockSummarySchema = z.object({
  siteId: z.string(),
  siteName: z.string(),
  currentQuantity: z.number(),
  unit: z.string(),
  reorderPoint: z.number(),
  storageLocation: z.string(),
  lastUpdated: z.string(),
});

export type StockSummary = z.infer<typeof stockSummarySchema>;

// Recent purchase order reference
export const recentPurchaseSchema = z.object({
  poId: z.string(),
  poNumber: z.string(),
  supplierName: z.string(),
  quantity: z.number(),
  unitPrice: z.number(),
  totalPrice: z.number(),
  orderDate: z.string(),
  deliveryDate: z.string().optional(),
  status: z.string(),
});

export type RecentPurchase = z.infer<typeof recentPurchaseSchema>;

export const catalogItemSchema = z.object({
  itemId: z.string(),
  sku: z.string(),
  internalCode: z.string().optional(),
  ean: z.string().optional(),
  upc: z.string().optional(),
  gtin: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  category: z.string(),
  subcategory: z.string().optional(),
  unit: z.string(),
  unitSize: z.number().optional(),
  unitCost: z.number().optional(),
  shelfLifeDays: z.number().optional(),
  storageType: z.enum(['frozen', 'chilled', 'dry', 'ambient']),
  allergens: z.array(z.string()),
  dietaryFlags: z.array(z.string()),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Extended PIM fields
  suppliers: z.array(itemSupplierSchema).optional(),
  costHistory: z.array(costHistoryEntrySchema).optional(),
  attachments: z.array(itemAttachmentSchema).optional(),
  relatedItems: z.array(relatedItemSchema).optional(),
  recipeUsage: z.array(recipeUsageSchema).optional(),
  stockLevels: z.array(stockSummarySchema).optional(),
  recentPurchases: z.array(recentPurchaseSchema).optional(),
});

export type CatalogItem = z.infer<typeof catalogItemSchema>;

export const catalogResponseSchema = z.object({
  data: z.array(catalogItemSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number().optional(),
  }),
});

export type CatalogResponse = z.infer<typeof catalogResponseSchema>;

export interface CatalogFilters {
  category?: string;
  search?: string;
  limit?: number;
}

export const STORAGE_TYPE_LABELS: Record<string, string> = {
  frozen: 'Frozen',
  chilled: 'Chilled',
  dry: 'Dry Storage',
  ambient: 'Ambient',
};

export const ALLERGEN_LABELS: Record<string, string> = {
  milk: 'Milk',
  eggs: 'Eggs',
  fish: 'Fish',
  shellfish: 'Shellfish',
  'tree-nuts': 'Tree Nuts',
  peanuts: 'Peanuts',
  wheat: 'Wheat',
  soybeans: 'Soybeans',
  sesame: 'Sesame',
};

export const DIETARY_FLAG_LABELS: Record<string, string> = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  'gluten-free': 'Gluten-Free',
  'dairy-free': 'Dairy-Free',
  halal: 'Halal',
  kosher: 'Kosher',
  organic: 'Organic',
};

export const RELATIONSHIP_TYPE_LABELS: Record<string, string> = {
  substitute: 'Substitute',
  similar: 'Similar Product',
  component: 'Component Of',
  parent: 'Parent Item',
};

export const RELATIONSHIP_TYPE_COLORS: Record<string, string> = {
  substitute: 'bg-blue-100 text-blue-800',
  similar: 'bg-purple-100 text-purple-800',
  component: 'bg-amber-100 text-amber-800',
  parent: 'bg-green-100 text-green-800',
};
